import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!token) {
      toast.error('Invalid reset link');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      
      setResetSuccess(true);
      toast.success('Password reset successfully! Redirecting to login...');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1679615845580-8691c78fd7d3?q=80&w=2000)',
          zIndex: 0
        }}
      ></div>
      
      {/* Overlay */}
      <div 
        className="absolute inset-0" 
        style={{ 
          background: 'linear-gradient(to bottom, rgba(10, 14, 26, 0.5) 0%, rgba(26, 35, 50, 0.6) 50%, rgba(45, 55, 72, 0.7) 100%)',
          zIndex: 1
        }}
      ></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-20 flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full">
          <Card className="glass-effect border-cyan-500/30">
            <CardContent className="p-8">
              {/* Logo */}
              <div className="text-center mb-6">
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/56npyh3g_11-11%20Coaching%20App.jpg" 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ color: '#331a67' }}>
                  Reset Password
                </h2>
                <p className="text-sm" style={{ color: '#445277' }}>
                  Enter your new password below
                </p>
              </div>

              {!resetSuccess ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#331a67' }}>
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#445277' }} />
                      <Input
                        id="reset-new-password"
                        name="new-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                        className="pl-10 pr-10 bg-white border-cyan-500/30 placeholder:text-gray-400"
                        style={{ color: '#000000' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#445277' }}>
                      Minimum 8 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#331a67' }}>
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#445277' }} />
                      <Input
                        id="reset-confirm-password"
                        name="confirm-new-password"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        className="pl-10 pr-10 bg-white border-cyan-500/30 placeholder:text-gray-400"
                        style={{ color: '#000000' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500">
                      Passwords do not match
                    </p>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full btn-primary" 
                    disabled={loading || !token}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>

                  {!token && (
                    <p className="text-xs text-red-500 text-center">
                      Invalid or missing reset token. Please request a new password reset link.
                    </p>
                  )}

                  <Link to="/">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="w-full"
                      style={{ color: '#331a67' }}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <CheckCircle className="w-16 h-16" style={{ color: '#00bcd4' }} />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ color: '#331a67' }}>
                    Password Reset Successfully!
                  </h3>
                  <p className="text-sm" style={{ color: '#445277' }}>
                    Your password has been updated. You can now log in with your new password.
                  </p>
                  <p className="text-xs" style={{ color: '#445277' }}>
                    Redirecting to login page...
                  </p>
                  <Link to="/">
                    <Button 
                      type="button" 
                      className="w-full btn-primary mt-4"
                    >
                      Go to Login
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
